// QuickSettingsContent — the React body of the Quick Settings modal (AC3,4,8,9,10,12,13,14,15).
//
// React LOCAL state only (no MobX). Props: { scanner, initialMap, onApply, onClose, onRun, revision }.
//   scanner  : () => row[]   (scanTunables; re-called when `revision` changes so the Dialog can
//                             force a rescan after a workspace load/clear — AC15)
//   initialMap: { id: value } optional seed for edited values
//   onApply  : (edits[]) => { applied, errors, banner, readBacks }   (applyEdits)
//   onClose  : () => void
//   onRun    : () => void    (optional; shows a Run button that applies then hands off to the
//                             real Run-button flow — mobile "tweak and go" shortcut)
//   revision : number        (bumped by the Dialog to invalidate after a reset)
//
// Thin view: logic lives in scanner/apply/validation. Labels render as React text nodes
// (auto-escaped; RFM3 guard). File kept <400 lines.
import React from 'react';
import PropTypes from 'prop-types';
import { translate } from '../../../common/i18n';
import { validateNumber, validateText } from './validation';

const QuickSettingsContent = ({ scanner, initialMap = {}, onApply, onClose, onRun, revision = 0 }) => {
    const [values, setValues] = React.useState({ ...initialMap });
    const [errors, setErrors] = React.useState({});
    const [formError, setFormError] = React.useState('');
    const [stale, setStale] = React.useState(false);
    const [success, setSuccess] = React.useState(false);
    const [showCloseConfirm, setShowCloseConfirm] = React.useState(false);
    const hadRowsRef = React.useRef(false);

    // Rescan when the scanner or revision changes (Dialog bumps revision on reset).
    const rows = React.useMemo(() => (typeof scanner === 'function' ? scanner() : []), [scanner, revision]);

    // AC15: detect the workspace being cleared while the modal is open (we had rows, now none).
    React.useEffect(() => {
        if (hadRowsRef.current && rows.length === 0) {
            setStale(true);
        }
        if (rows.length > 0) {
            hadRowsRef.current = true;
        }
    }, [rows]);

    const dirty = rows.some(r => values[r.id] !== undefined && String(values[r.id]) !== String(r.value));
    const actionable = rows.filter(r => !r.orphan); // AC9: orphans flagged but excluded from Apply
    const hasErrors = Object.values(errors).some(Boolean);
    const applyDisabled = stale || actionable.length === 0 || hasErrors;

    const setValue = (row, raw) => {
        setSuccess(false);
        setValues(v => ({ ...v, [row.id]: raw }));
        const res = row.kind === 'number' ? validateNumber(raw, { min: row.min, max: row.max }) : validateText(raw);
        setErrors(e => ({ ...e, [row.id]: res.ok ? '' : res.error }));
    };

    const applyPendingEdits = () => {
        if (applyDisabled) {
            return null;
        }
        setSuccess(false);
        const edits = actionable.map(r => ({
            id: r.id,
            type: r.kind,
            field: r.field,
            value: values[r.id] !== undefined ? values[r.id] : r.value,
            min: r.min,
            max: r.max,
        }));
        const result = onApply(edits);
        setFormError(result.errors && result.errors.length ? result.banner : '');
        return result;
    };

    const handleApply = () => {
        const result = applyPendingEdits();
        if (result && !(result.errors && result.errors.length)) {
            setSuccess(true);
        }
    };

    // Run: apply pending edits, then hand off to the real Run button flow (required-blocks /
    // trade-limit / reality-check gates all live there — this never bypasses them).
    const handleRun = () => {
        const result = applyPendingEdits();
        if (!result || (result.errors && result.errors.length)) {
            return; // apply failed — stay open, error banner shown
        }
        if (typeof onClose === 'function') {
            onClose(); // values just applied, nothing to discard — skip the dirty-confirm gate
        }
        if (typeof onRun === 'function') {
            onRun();
        }
    };

    const handleClose = () => {
        if (dirty && !showCloseConfirm) {
            setShowCloseConfirm(true);
            return;
        }
        setShowCloseConfirm(false);
        if (typeof onClose === 'function') {
            onClose();
        }
    };

    const cancelClose = () => setShowCloseConfirm(false);

    // AC15 stale state: workspace cleared under us — show banner, disable Apply, no writes.
    if (stale) {
        return (
            <div className="qs-stale" data-testid="qs-stale">
                <span>{translate('Workspace changed — close and reopen Quick Settings.')}</span>
                <button type="button" className="qs-close" onClick={handleClose}>
                    {translate('Close')}
                </button>
            </div>
        );
    }

    // AC12 empty state: zero tunable blocks.
    if (rows.length === 0) {
        return (
            <div className="qs-empty" data-testid="qs-empty">
                <span>{translate('No tunable blocks on the workspace.')}</span>
                <button type="button" className="qs-apply" disabled>
                    {translate('Apply')}
                </button>
            </div>
        );
    }

    return (
        <div className="qs-content">
            {success && (
                // AC14 success notice — banner is the literal 'Quick Settings applied' from apply.js.
                <div className="qs-success" data-testid="qs-success">
                    {formError || translate('Quick Settings applied')}
                </div>
            )}
            {formError && !success && (
                <div className="qs-formerror" data-testid="qs-formerror">
                    {formError}
                </div>
            )}
            {rows.map((r, i) => (
                <React.Fragment key={r.id}>
                    {r.group && (i === 0 || rows[i - 1].group !== r.group) && (
                        <div className="qs-group">{r.group}</div>
                    )}
                    <div
                        className="qs-row"
                        data-rowid={r.id}
                        data-orphan={r.orphan ? '1' : '0'}
                        data-kind={r.kind}
                    >
                        <span className="qs-label">
                            {r.label}
                            {r.disambiguator && (
                                <em className="qs-disamb" data-testid={`qs-disamb-${r.id}`}>
                                    {` (${r.disambiguator.slice(-4)})`}
                                </em>
                            )}
                        </span>
                        {r.orphan && (
                            <span className="qs-orphan-badge" data-testid={`qs-orphan-${r.id}`}>
                                {translate('orphan')}
                            </span>
                        )}
                        {r.kind === 'number' ? (
                            <input
                                className="qs-input qs-number"
                                data-id={r.id}
                                type="number"
                                inputMode="decimal"
                                value={values[r.id] !== undefined ? values[r.id] : r.value}
                                onChange={e => setValue(r, e.target.value)}
                            />
                        ) : (
                            <input
                                className="qs-input qs-text"
                                data-id={r.id}
                                type="text"
                                value={values[r.id] !== undefined ? values[r.id] : r.value}
                                onChange={e => setValue(r, e.target.value)}
                            />
                        )}
                        {errors[r.id] && (
                            <span className="qs-error" data-testid={`qs-error-${r.id}`}>
                                {errors[r.id]}
                            </span>
                        )}
                    </div>
                </React.Fragment>
            ))}
            {showCloseConfirm && (
                <div className="qs-confirm" data-testid="qs-confirm">
                    <span>{translate('Discard changes?')}</span>
                    <button type="button" className="qs-confirm-yes" onClick={handleClose}>
                        {translate('Yes')}
                    </button>
                    <button type="button" className="qs-confirm-no" onClick={cancelClose}>
                        {translate('No')}
                    </button>
                </div>
            )}
            <div className="qs-actions">
                <button type="button" className="qs-apply" disabled={applyDisabled} onClick={handleApply}>
                    {translate('Apply')}
                </button>
                {typeof onRun === 'function' && (
                    <button type="button" className="qs-run" disabled={applyDisabled} onClick={handleRun}>
                        {translate('Run')}
                    </button>
                )}
                <button type="button" className="qs-close" onClick={handleClose}>
                    {translate('Close')}
                </button>
            </div>
        </div>
    );
};

QuickSettingsContent.propTypes = {
    scanner: PropTypes.func.isRequired,
    initialMap: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    onApply: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    onRun: PropTypes.func,
    revision: PropTypes.number,
};

QuickSettingsContent.defaultProps = {
    initialMap: {},
    onClose: undefined,
    onRun: undefined,
    revision: 0,
};

export default QuickSettingsContent;
